import * as React from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';

interface Props {
  children: React.ReactNode;
  /** How strongly the element pulls toward the cursor. 0..1. */
  strength?: number;
  /** Extra classes on the outer span. */
  className?: string;
  /** Forwarded onClick. */
  onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
}

/**
 * Magnetic-pull wrapper. Renders an inline-block span that translates a fraction
 * of the cursor offset on mouse move and springs back to zero on leave.
 *
 * Crucially: uses Framer Motion values OUTSIDE the React render cycle (no
 * useState on mouse move) so it stays smooth even on weaker devices.
 */
export function MagneticButton({ children, strength = 0.22, className, onClick }: Props) {
  const ref = React.useRef<HTMLSpanElement>(null);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  const x = useSpring(mx, { stiffness: 220, damping: 20, mass: 0.7 });
  const y = useSpring(my, { stiffness: 220, damping: 20, mass: 0.7 });

  // gentle counter-rotate so the element tilts subtly toward the cursor
  const rx = useTransform(my, [-30, 30], [4, -4]);
  const ry = useTransform(mx, [-30, 30], [-6, 6]);

  function onMove(e: React.MouseEvent<HTMLSpanElement>) {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    mx.set((e.clientX - cx) * strength);
    my.set((e.clientY - cy) * strength);
  }
  function onLeave() {
    mx.set(0);
    my.set(0);
  }

  return (
    <motion.span
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onClick={onClick}
      style={{ x, y, rotateX: rx, rotateY: ry, transformPerspective: 800 }}
      className={['inline-block will-change-transform', className ?? ''].join(' ')}
    >
      {children}
    </motion.span>
  );
}
